import React, { useState, useEffect, useRef } from 'react';

import {
    ClipboardList, X, RefreshCw, Pencil, Copy, Trash2,
    ChevronLeft, ChevronRight, ChevronUp, ChevronDown,
    Globe, MessageSquare, MessageCircle, Facebook, Archive,
} from 'lucide-react';
import ConfirmModal from './ConfirmModal';

/**
 * SurveyBrowser
 *
 * Full-screen modal for browsing, filtering, editing, duplicating, and deleting
 * all surveys. Modeled after RecipientBrowser.
 *
 * Props:
 *   isOpen          - whether the modal is visible
 *   onClose         - callback to close
 *   surveys         - array of survey objects from App.js state
 *   deployments     - array of deployment objects
 *   onRefresh       - callback to refresh survey list
 *   openDesigner    - callback to open survey in designer
 *   onDeleteSurvey  - callback to delete a survey
 *   onCopy          - callback to duplicate a survey
 */
export default function SurveyBrowser({
    isOpen, onClose, surveys, deployments,
    onRefresh, openDesigner, onDeleteSurvey, onCopy,
    onArchiveSurvey, onBulkArchiveSurveys,
}) {
    // Filters
    const [searchQ, setSearchQ] = useState('');
    const [sortBy, setSortBy] = useState('updated_at');
    const [sortOrder, setSortOrder] = useState('DESC');
    const [page, setPage] = useState(1);
    const perPage = 50;

    // Selection
    const [selectedIds, setSelectedIds] = useState(new Set());



    // Delete confirmation
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [confirmModal, setConfirmModal] = useState(null);

    // Loading/error (local operations)
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const searchRef = useRef(null);
    const searchDebounceRef = useRef(null);

    // Reset on open
    useEffect(() => {
        if (isOpen) {
            setSearchQ('');
            setPage(1);
            setSelectedIds(new Set());
            setConfirmDeleteId(null);
            setError('');
            setTimeout(() => searchRef.current?.focus(), 100);
        }
    }, [isOpen]);

    // Close on Escape
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [isOpen, onClose]);

    // ─── Derived data ───

    // Deployment status map: surveyId → { deployed: bool, deploymentCount }
    const deploymentMap = {};
    (deployments || []).forEach(d => {
        if (!deploymentMap[d.survey_id]) {
            deploymentMap[d.survey_id] = { deployed: false, count: 0 };
        }
        deploymentMap[d.survey_id].count++;
        if (d.is_open) deploymentMap[d.survey_id].deployed = true;
    });

    // Parse tags from survey (JSONB → array of strings)
    const parseTags = (s) => {
        if (!s.tags) return [];
        try {
            const t = typeof s.tags === 'string' ? JSON.parse(s.tags) : s.tags;
            return Array.isArray(t) ? t : [];
        } catch { return []; }
    };

    // Filter
    const filtered = (surveys || []).filter(s => {
        if (!searchQ) return true;
        const q = searchQ.toLowerCase();
        const name = (s.name || s.title || '').toLowerCase();
        const org = (s.organization || '').toLowerCase();
        const tags = parseTags(s).join(' ').toLowerCase();
        return name.includes(q) || org.includes(q) || tags.includes(q) ||
            (s.survey_id || s.id || '').toLowerCase().includes(q);
    });

    // Sort
    const sorted = [...filtered].sort((a, b) => {
        let va, vb;
        switch (sortBy) {
            case 'name':
                va = (a.name || a.title || '').toLowerCase();
                vb = (b.name || b.title || '').toLowerCase();
                break;
            case 'status':
                va = getStatus(a);
                vb = getStatus(b);
                break;
            case 'organization':
                va = (a.organization || '').toLowerCase();
                vb = (b.organization || '').toLowerCase();
                break;
            case 'updated_at':
            default:
                va = a.updated_at || a.created_at || '';
                vb = b.updated_at || b.created_at || '';
                break;
        }
        if (va < vb) return sortOrder === 'ASC' ? -1 : 1;
        if (va > vb) return sortOrder === 'ASC' ? 1 : -1;
        return 0;
    });

    // Paginate
    const totalPages = Math.ceil(sorted.length / perPage);
    const paged = sorted.slice((page - 1) * perPage, page * perPage);

    function getStatus(s) {
        const sid = s.survey_id || s.id;
        const dep = deploymentMap[sid];
        if (dep?.deployed) return 'deployed';
        if (dep?.count > 0) return 'closed';
        return s.status || 'draft';
    }

    function getSurveyId(s) {
        return s.survey_id || s.id;
    }

    // ─── Handlers ───

    const handleSearchChange = (val) => {
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setSearchQ(val);
            setPage(1);
        }, 300);
    };

    const handleSort = (col) => {
        if (sortBy === col) {
            setSortOrder(o => o === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortBy(col);
            setSortOrder('ASC');
        }
        setPage(1);
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === paged.length && paged.length > 0) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(paged.map(s => getSurveyId(s))));
        }
    };

    // Parse target_channels JSONB field → string[]
    const parseTargetChannels = (s) => {
        if (!s.target_channels) return [];
        try {
            const ch = typeof s.target_channels === 'string' ? JSON.parse(s.target_channels) : s.target_channels;
            return Array.isArray(ch) ? ch : [];
        } catch { return []; }
    };

    // Parse survey language from settings JSONB
    const parseSurveyLanguage = (s) => {
        try {
            const settings = typeof s.settings === 'string' ? JSON.parse(s.settings) : (s.settings || {});
            return settings.language || settings.target_language || null;
        } catch { return null; }
    };

    const CHANNEL_ICONS = {
        web: { Icon: Globe, label: 'Web' },
        browser: { Icon: Globe, label: 'Browser' },
        sms: { Icon: MessageSquare, label: 'SMS' },
        whatsapp: { Icon: MessageCircle, label: 'WhatsApp' },
        messenger: { Icon: Facebook, label: 'Messenger' },
    };

    const channelBadges = (s) => {
        const chs = parseTargetChannels(s);
        if (chs.length === 0) return <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>;
        return (
            <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}>
                {chs.map(c => {
                    const cfg = CHANNEL_ICONS[c.toLowerCase()];
                    if (cfg) {
                        const { Icon, label } = cfg;
                        return <Icon key={c} size={14} title={label} color="#5B9FFF" strokeWidth={1.5} />;
                    }
                    return <span key={c} style={{ fontSize: 11, color: '#5F6368' }}>{c}</span>;
                })}
            </span>
        );
    };


    // Duplicate
    const handleDuplicate = async (sid) => {
        setLoading(true);
        setError('');
        try {
            await onCopy?.(sid);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Bulk duplicate
    const handleBulkDuplicate = async () => {
        if (selectedIds.size === 0) return;
        setLoading(true);
        setError('');
        try {
            for (const sid of selectedIds) {
                await onCopy?.(sid);
            }
            setSelectedIds(new Set());
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Delete
    const handleDelete = async (sid) => {
        setLoading(true);
        setError('');
        try {
            await onDeleteSurvey?.(sid);
            setConfirmDeleteId(null);
            setSelectedIds(prev => {
                const next = new Set(prev);
                next.delete(sid);
                return next;
            });
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Bulk delete
    const handleBulkDelete = async () => {
        if (selectedIds.size === 0) return;
        setConfirmModal({
            title: 'Delete surveys?',
            message: `Delete ${selectedIds.size} survey(s)? This action cannot be undone.`,
            confirmLabel: 'Delete',
            danger: true,
            onConfirm: async () => {
                setConfirmModal(null);
                setLoading(true);
                setError('');
                try {
                    for (const sid of selectedIds) {
                        await onDeleteSurvey?.(sid);
                    }
                    setSelectedIds(new Set());
                } catch (err) {
                    setError(err.message);
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    // Archive
    const handleArchive = async (sid) => {
        setLoading(true);
        setError('');
        try {
            await onArchiveSurvey?.(sid);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Bulk archive
    const handleBulkArchive = async () => {
        if (selectedIds.size === 0) return;
        setLoading(true);
        setError('');
        try {
            await onBulkArchiveSurveys?.([...selectedIds]);
            setSelectedIds(new Set());
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // ─── Status badge ───
    const statusBadge = (s) => {
        const st = getStatus(s);
        const colors = {
            deployed: { bg: '#E8F5E9', color: '#2E7D32', border: '#81C784' },
            closed: { bg: '#FFF9C4', color: '#F57F17', border: '#FFD54F' },
            draft: { bg: 'var(--color-bg-base)', color: '#3C4043', border: '#DADCE0' },
            published: { bg: '#E3F2FD', color: '#1565C0', border: '#90CAF9' },
        };
        const c = colors[st] || colors.draft;
        return (
            <span style={{
                display: 'inline-block', padding: '2px 8px', borderRadius: 10,
                fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                background: c.bg, color: c.color, border: `1px solid ${c.border}`,
            }}>
                {st}
            </span>
        );
    };

    // ─── Tag badges ───
    const tagBadges = (s) => {
        const tags = parseTags(s);
        if (tags.length === 0) return <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>;
        const tagColors = ['#5B9FFF', '#BDC1C6', '#E91E63', '#FFC107', '#4CAF50', '#F44336', '#5C6BC0'];
        return tags.map((t, i) => (
            <span key={i} style={{
                display: 'inline-block', padding: '1px 8px', borderRadius: 10,
                fontSize: 11, fontWeight: 500, marginRight: 4, marginBottom: 2,
                background: `${tagColors[i % tagColors.length]}18`,
                color: tagColors[i % tagColors.length],
                border: `1px solid ${tagColors[i % tagColors.length]}40`,
            }}>
                {t}
            </span>
        ));
    };

    // ─── Render ───

    if (!isOpen) return null;

    const sortArrow = (col) => {
        if (sortBy !== col) return null;
        return sortOrder === 'ASC'
            ? <ChevronUp size={14} style={{ verticalAlign: 'middle', marginLeft: 2 }} />
            : <ChevronDown size={14} style={{ verticalAlign: 'middle', marginLeft: 2 }} />;
    };

    return (
        <div style={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div style={styles.modal} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div style={styles.header}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}><ClipboardList size={22} /> Survey Browser</h2>
                        <span style={styles.badge}>{filtered.length} survey{filtered.length !== 1 ? 's' : ''}</span>
                    </div>
                    <button onClick={onClose} style={styles.closeBtn} title="Close"><X size={20} /></button>
                </div>

                {/* Error banner */}
                {error && (
                    <div style={{ padding: '8px 20px', background: '#FFEBEE', borderBottom: '1px solid #FFCDD2', color: '#C62828', fontSize: 13 }}>
                        {error}
                        <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#C62828', display: 'inline-flex', alignItems: 'center' }}><X size={16} /></button>
                    </div>
                )}

                {/* Filter bar */}
                <div style={styles.filterBar}>
                    <input
                        ref={searchRef}
                        style={styles.searchInput}
                        placeholder="Search by name, organization, tags…"
                        defaultValue={searchQ}
                        onChange={e => handleSearchChange(e.target.value)}
                    />
                    <button onClick={() => onRefresh?.()} style={{ ...styles.actionBtn, display: 'inline-flex', alignItems: 'center', gap: 6 }} disabled={loading}>
                        <RefreshCw size={14} /> Refresh
                    </button>
                </div>

                {/* Table */}
                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.th, width: 36 }}>
                                    <input
                                        type="checkbox"
                                        checked={paged.length > 0 && selectedIds.size === paged.length}
                                        onChange={toggleSelectAll}
                                    />
                                </th>
                                <th style={styles.th} onClick={() => handleSort('name')}>
                                    Name{sortArrow('name')}
                                </th>
                                <th style={{ ...styles.th, width: 90 }} onClick={() => handleSort('status')}>
                                    Status{sortArrow('status')}
                                </th>
                                <th style={{ ...styles.th, width: 160 }} onClick={() => handleSort('organization')}>
                                    Organization{sortArrow('organization')}
                                </th>
                                <th style={{ ...styles.th, width: 180 }}>
                                    Tags
                                </th>
                                <th style={{ ...styles.th, width: 90 }}>
                                    Channels
                                </th>
                                <th style={{ ...styles.th, width: 80 }}>
                                    Language
                                </th>
                                <th style={{ ...styles.th, width: 140 }} onClick={() => handleSort('updated_at')}>
                                    Updated{sortArrow('updated_at')}
                                </th>
                                <th style={{ ...styles.th, width: 100, textAlign: 'center' }}>
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {paged.length === 0 && (
                                <tr>
                                    <td colSpan={9} style={styles.emptyCell}>
                                        {searchQ ? 'No surveys match your search.' : 'No surveys found.'}
                                    </td>
                                </tr>
                            )}
                            {paged.map(s => {
                                const sid = getSurveyId(s);
                                const isConfirmingDelete = confirmDeleteId === sid;
                                const lang = parseSurveyLanguage(s);
                                return (
                                    <tr key={sid} style={{
                                        ...styles.row,
                                        background: selectedIds.has(sid) ? '#E3F2FD' : undefined,
                                    }}>
                                        {/* Checkbox */}
                                        <td style={styles.td}>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(sid)}
                                                onChange={() => toggleSelect(sid)}
                                            />
                                        </td>

                                        {/* Name */}
                                        <td style={styles.td}>
                                            <span style={{ fontWeight: 500, color: '#202124' }}>
                                                {s.name || s.title || '(untitled)'}
                                            </span>
                                        </td>

                                        {/* Status */}
                                        <td style={styles.td}>{statusBadge(s)}</td>

                                        {/* Organization */}
                                        <td style={styles.td}>
                                            <span style={{ color: s.organization ? '#3C4043' : '#9ca3af' }}>
                                                {s.organization || '—'}
                                            </span>
                                        </td>

                                        {/* Tags */}
                                        <td style={styles.td}>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                                {tagBadges(s)}
                                            </div>
                                        </td>

                                        {/* Channels */}
                                        <td style={styles.td}>{channelBadges(s)}</td>

                                        {/* Language */}
                                        <td style={{ ...styles.td, fontSize: 12, color: lang ? '#3C4043' : '#9ca3af' }}>
                                            {lang || '—'}
                                        </td>

                                        {/* Updated */}
                                        <td style={{ ...styles.td, fontSize: 12, color: '#5F6368' }}>
                                            {s.updated_at ? new Date(s.updated_at).toLocaleDateString('en-US', {
                                                month: 'short', day: 'numeric', year: 'numeric',
                                                hour: '2-digit', minute: '2-digit',
                                            }) : '—'}
                                        </td>

                                        {/* Actions */}
                                        <td style={{ ...styles.td, textAlign: 'center' }}>
                                            {isConfirmingDelete ? (
                                                <div style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                                    padding: '2px 6px', borderRadius: 6,
                                                    background: '#FFEBEE', border: '1px solid #FFCDD2',
                                                    fontSize: 11, whiteSpace: 'nowrap',
                                                }}>
                                                    <span style={{ color: '#C62828' }}>Delete?</span>
                                                    <button
                                                        onClick={() => handleDelete(sid)}
                                                        style={{
                                                            background: '#F44336', color: '#fff', border: 'none',
                                                            borderRadius: 3, padding: '1px 6px', cursor: 'pointer',
                                                            fontSize: 11, fontWeight: 600,
                                                        }}
                                                    >Yes</button>
                                                    <button
                                                        onClick={() => setConfirmDeleteId(null)}
                                                        style={{
                                                            background: '#fff', border: '1px solid #DADCE0',
                                                            borderRadius: 3, padding: '1px 6px', cursor: 'pointer',
                                                            fontSize: 11,
                                                        }}
                                                    >No</button>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                                                    <button onClick={() => { openDesigner?.(sid); onClose(); }} style={styles.editBtn} title={deploymentMap[sid]?.deployed ? "Undeploy to edit" : "Open in Designer"} disabled={!!deploymentMap[sid]?.deployed}><Pencil size={16} /></button>
                                                    <button onClick={() => handleDuplicate(sid)} style={styles.editBtn} title="Duplicate" disabled={loading}><Copy size={16} /></button>
                                                    <button onClick={() => handleArchive(sid)} style={styles.editBtn} title="Archive" disabled={loading || !!deploymentMap[sid]?.deployed}><Archive size={16} /></button>
                                                    <button onClick={() => setConfirmDeleteId(sid)} style={{ ...styles.editBtn, color: '#F44336' }} title="Delete"><Trash2 size={16} /></button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div style={styles.pagination}>
                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} style={{ ...styles.pageBtn, display: 'inline-flex', alignItems: 'center', gap: 4 }}><ChevronLeft size={14} /> Prev</button>
                        <span style={{ fontSize: 13, color: '#5F6368' }}>Page {page} of {totalPages}</span>
                        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={{ ...styles.pageBtn, display: 'inline-flex', alignItems: 'center', gap: 4 }}>Next <ChevronRight size={14} /></button>
                    </div>
                )}

                {/* Bulk action bar (visible when items selected) */}
                {selectedIds.size > 0 && (
                    <div style={styles.bulkBar}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>
                            {selectedIds.size} survey{selectedIds.size !== 1 ? 's' : ''} selected
                        </span>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={handleBulkDuplicate} style={{ ...styles.actionBtn, display: 'inline-flex', alignItems: 'center', gap: 6 }} disabled={loading}>
                                <Copy size={14} /> Duplicate
                            </button>
                            <button onClick={handleBulkArchive} style={{ ...styles.actionBtn, display: 'inline-flex', alignItems: 'center', gap: 6 }} disabled={loading}>
                                <Archive size={14} /> Archive
                            </button>
                            <button onClick={handleBulkDelete} style={{ ...styles.actionBtn, color: '#F44336', borderColor: '#FFCDD2', display: 'inline-flex', alignItems: 'center', gap: 6 }} disabled={loading}>
                                <Trash2 size={14} /> Delete
                            </button>
                        </div>
                    </div>
                )}
            </div>
            <ConfirmModal
                open={!!confirmModal}
                title={confirmModal?.title}
                message={confirmModal?.message}
                confirmLabel={confirmModal?.confirmLabel}
                danger={confirmModal?.danger}
                onConfirm={confirmModal?.onConfirm}
                onCancel={() => setConfirmModal(null)}
            />
        </div>
    );
}

const styles = {
    overlay: {
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 3000,
    },
    modal: {
        background: '#fff', borderRadius: 12, width: '92vw', maxWidth: 1100,
        height: '88vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
    },
    header: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '16px 20px', borderBottom: '1px solid #E8EAED',
    },
    badge: {
        background: '#E3F2FD', color: '#5B9FFF', padding: '2px 10px',
        borderRadius: 12, fontSize: 13, fontWeight: 500,
    },
    closeBtn: {
        background: 'transparent', border: 'none', fontSize: 20,
        cursor: 'pointer', padding: 4, color: '#5F6368', lineHeight: 1,
    },
    filterBar: {
        display: 'flex', gap: 8, padding: '12px 20px',
        borderBottom: '1px solid #F7F9FB', flexWrap: 'wrap', alignItems: 'center',
    },
    searchInput: {
        flex: 1, minWidth: 200, padding: '6px 10px', borderRadius: 6,
        border: '1px solid #DADCE0', fontSize: 13, outline: 'none',
    },
    actionBtn: {
        padding: '4px 12px', borderRadius: 6, border: '1px solid #DADCE0',
        fontSize: 12, cursor: 'pointer', fontWeight: 500, background: '#fff',
    },
    tableContainer: {
        flex: 1, overflow: 'auto', padding: '0 20px',
    },
    table: {
        width: '100%', borderCollapse: 'collapse', fontSize: 13,
    },
    th: {
        padding: '8px 6px', textAlign: 'left', borderBottom: '2px solid #E8EAED',
        cursor: 'pointer', fontWeight: 600, fontSize: 12, color: '#3C4043',
        position: 'sticky', top: 0, background: '#fff', userSelect: 'none',
    },
    td: {
        padding: '6px 6px', borderBottom: '1px solid #F7F9FB',
    },
    row: {
        cursor: 'pointer', transition: 'background 0.1s',
    },
    emptyCell: {
        padding: 24, textAlign: 'center', color: '#9ca3af',
    },
    editInput: {
        width: '100%', padding: '4px 6px', border: '1px solid #DADCE0', borderRadius: 4, fontSize: 13,
    },
    editBtn: {
        background: 'none', border: '1px solid #DADCE0', borderRadius: 4,
        padding: '4px 8px', cursor: 'pointer', color: '#5F6368',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        transition: 'background 0.15s, border-color 0.15s',
    },

    pagination: {
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        gap: 16, padding: '12px 20px', borderTop: '1px solid #E8EAED',
    },
    pageBtn: {
        padding: '4px 12px', borderRadius: 6, border: '1px solid #DADCE0',
        fontSize: 12, cursor: 'pointer', background: '#fff',
    },
    bulkBar: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '10px 20px',
        background: 'var(--color-bg-base)', borderTop: '2px solid #5B9FFF',
        borderRadius: '0 0 12px 12px',
    },
};
